import type { UniqueIdentifier } from '@dnd-kit/core';
import type { BoardColumn } from './boardColumns';

/**
 * The drop, as a pure function.
 *
 * A drag ends with two opaque dnd ids and a question: which record has to be
 * written, and with which value? Answering that here - not inside an
 * `onDragEnd` closure - is what makes the move testable without a DOM, a
 * pointer or layout: happy-dom has no geometry, so a test that "drags" proves
 * nothing about the decision the board makes.
 *
 * Ids are PREFIXED strings rather than bare record ids, because cards and
 * columns share one id space in dnd-kit and a record id that happened to equal
 * an option id would otherwise be ambiguous.
 */
const CARD_PREFIX = 'card:';
const COLUMN_PREFIX = 'column:';

export function cardDragId(recordId: string): string {
  return `${CARD_PREFIX}${recordId}`;
}

export function columnDropId(columnId: string): string {
  return `${COLUMN_PREFIX}${columnId}`;
}

function strip(id: UniqueIdentifier | null | undefined, prefix: string): string | null {
  // dnd-kit's UniqueIdentifier is `string | number`; ours are always strings, so
  // anything else is an id this board did not mint.
  if (typeof id !== 'string' || !id.startsWith(prefix)) {
    return null;
  }
  const rest = id.slice(prefix.length);
  return rest.length > 0 ? rest : null;
}

export function recordIdOfDragId(id: UniqueIdentifier | null | undefined): string | null {
  return strip(id, CARD_PREFIX);
}

export function columnIdOfDropId(id: UniqueIdentifier | null | undefined): string | null {
  return strip(id, COLUMN_PREFIX);
}

export interface MoveTargetInput {
  readonly activeId: UniqueIdentifier | null | undefined;
  /** `undefined` when the card was released outside every column. */
  readonly overId: UniqueIdentifier | null | undefined;
  readonly columnOfRecord: ReadonlyMap<string, string>;
  readonly columns: readonly BoardColumn[];
}

export interface MoveTarget {
  readonly recordId: string;
  readonly fromColumnId: string;
  readonly toColumnId: string;
  /** The value to write into the group field. `null` empties it. */
  readonly optionId: string | null;
}

/**
 * `null` means "no write": released outside the board, released where it
 * started, or ids the board does not recognise. A zero-change update would
 * still be a write attempt - INV-4 wants no event for it, and the user would
 * see an approval toast for a move they did not make.
 */
export function moveTarget(input: MoveTargetInput): MoveTarget | null {
  const recordId = recordIdOfDragId(input.activeId);
  const toColumnId = columnIdOfDropId(input.overId);
  if (recordId === null || toColumnId === null) {
    return null;
  }

  const fromColumnId = input.columnOfRecord.get(recordId);
  if (fromColumnId === undefined || fromColumnId === toColumnId) {
    return null;
  }

  const column = input.columns.find((candidate) => candidate.id === toColumnId);
  if (column === undefined) {
    return null;
  }

  return { recordId, fromColumnId, toColumnId, optionId: column.optionId };
}
